import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "461",
  "slug": "how-to-spot-an-airport-from-satellite-imagery",
  "title": "How to Spot an Airport from Satellite Imagery",
  "excerpt": "Airports are some of the easiest human structures to identify from space. Here is how to read runways, taxiways, and terminals — and use them to figure out where you are.",
  "date": "2026-06-16",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1683971336619-d445cbec0276?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhaXJwb3J0JTIwcnVud2F5JTIwYWVyaWFsfGVufDF8MHx8fDE3ODE1OTc2Njh8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Airports are easy to spot from above thanks to their long, straight runways marked with large numbers, branching taxiways, and clusters of parked aircraft. Reading those features even tells you the prevailing wind and whether you are looking at a major hub or a small airstrip.",
  "content": [
    {
      "type": "paragraph",
      "text": "If you ever find yourself dropped into a satellite image with no idea where you are, an airport is one of the best landmarks you can hope to find. Airports are enormous, geometric, and built to a global standard, which makes them some of the most recognizable human structures on the entire planet from above. Better still, they are full of clues about exactly where you are."
    },
    {
      "type": "paragraph",
      "text": "Learn to read one and you add a powerful tool to your location-guessing kit. Here is what to look for."
    },
    {
      "type": "heading",
      "text": "The Runway Signature"
    },
    {
      "type": "paragraph",
      "text": "The runway is the heart of every airport and the first thing to find. Look for long, straight, flat strips, usually grey or dark asphalt, far longer than any road and perfectly level. They are marked in a way nothing else is: large white numbers at each end, a dashed centreline running the full length, and bold threshold stripes that look like a piano keyboard at the ends."
    },
    {
      "type": "paragraph",
      "text": "Those numbers are not random. A runway is numbered for its magnetic compass heading with the last digit dropped, so a runway pointing roughly 90 degrees is labelled 09 and the opposite end, pointing 270 degrees, is labelled 27. Spotting that pairing confirms instantly that you are looking at a runway and not a highway."
    },
    {
      "type": "heading",
      "text": "The Supporting Cast"
    },
    {
      "type": "paragraph",
      "text": "Around the runways, a few other features complete the picture and help you gauge the airport’s size."
    },
    {
      "type": "list",
      "items": [
        "Taxiways — narrower paved paths, often painted with yellow centrelines, that connect the runways to the terminals and parking areas.",
        "Aprons and ramps — large open paved zones where aircraft park, load, and refuel.",
        "Terminals — big distinctive buildings, frequently with finger-like piers or jet bridges reaching out to the parked planes.",
        "Aircraft — the planes themselves, whose familiar shape and known size make a handy ruler for judging scale."
      ]
    },
    {
      "type": "heading",
      "text": "Reading an Airport for Location Clues"
    },
    {
      "type": "paragraph",
      "text": "Once you have confirmed an airport, it starts telling you about its surroundings. The orientation of the runways is a clue to the local climate, because runways are aligned with the prevailing wind so aircraft can take off and land into it. The sheer scale matters too: a sprawling field with multiple parallel runways and dozens of gates signals a major international hub near a big city, while a single short strip suggests a small regional or rural airfield."
    },
    {
      "type": "paragraph",
      "text": "Even the land around the airport speaks. Dense urban sprawl, desert, tropical forest, or snow at the edges of the apron all narrow down which part of the world you are in."
    },
    {
      "type": "heading",
      "text": "Big Hub or Little Airstrip?"
    },
    {
      "type": "paragraph",
      "text": "Airports span an enormous range, and placing where one sits on that spectrum sharpens your guess. A handful of giant hubs have several long runways, vast terminal complexes, and aircraft parked nose to tail. At the other extreme, countless small airfields are little more than a single paved or even grass strip with a few light planes and a hangar. The size and complexity of what you see hints at the population and wealth of the region around it."
    },
    {
      "type": "heading",
      "text": "Why Runways Point the Way They Do"
    },
    {
      "type": "paragraph",
      "text": "It is worth dwelling on runway orientation, because it carries so much information. Aircraft prefer to take off and land into the wind, so runways are aligned with the direction the wind most often blows at that location. Spotting which way the runways run is, in effect, reading the prevailing wind of an entire region straight off the map."
    },
    {
      "type": "paragraph",
      "text": "At busy airports you will often see two or even three parallel runways side by side. These share the same heading but are labelled left, right, and centre, so the numbers read like 27L, 27R, and 27C. Several parallel strips are a strong sign you are looking at a major hub built to land many planes at once, another clue to the size of the city nearby."
    },
    {
      "type": "heading",
      "text": "Watch for Look-Alikes"
    },
    {
      "type": "paragraph",
      "text": "A few things can fool you at first glance. Wide, straight highways can resemble runways until you notice they lack the numbers, threshold markings, and connecting taxiways. Long, dark rectangles might turn out to be solar farms or even drag-racing strips. The trick is to confirm the full package: a true airport has the runway markings, the branching taxiways, the parking aprons, and usually a plane or two for proof."
    }
  ]
};
